import { useEffect, useState, useMemo } from 'react'
import { ordersApi, customersApi, sellerProductsApi } from '../../services/api'
import { getCurrentPositionSafe } from '../../utils/geolocation'
import ExportBar, { ExportColumn } from '../../components/ExportBar'

interface Customer {
  id: number
  name: string
  nickname: string | null
  district: string | null
  phone: string | null
  payment_mode: 'cash' | 'credit'
}

interface SellerProduct {
  id: number
  product_id: number
  name: string
  presentation: string
  recommended_price: number
  category: string
  real_price: number
}

type ItemStatus = 'pactado' | 'entregado' | 'cancelado'

interface OrderItem {
  id: number
  order_id: number
  seller_product_id: number
  product_name: string
  presentation: string
  quantity: number
  unit_price: number
  status: ItemStatus
  cancellation_reason: string | null
  sale_id: number | null
}

interface Order {
  id: number
  seller_id: number
  seller_name: string
  customer_id: number | null
  customer_name: string | null
  customer_district: string | null
  agreed_date: string
  promised_date: string | null
  notes: string | null
  created_at: string
  items: OrderItem[]
}

type Step = 'customer' | 'product' | 'review'

interface CartItem {
  seller_product_id: number
  product_name: string
  presentation: 'granel' | '250gr' | '1kg'
  quantity: number
  unit_price: number
  recommended_price: number
}

interface OrderForm {
  customer_id: number | null
  customer_search: string
  new_customer_name: string
  new_customer_phone: string
  new_customer_address: string
  new_customer_district: string
  new_customer_dni: string
  new_customer_ruc: string
  new_customer_payment_mode: 'cash' | 'credit'
  seller_product_id: number | null
  presentation: 'granel' | '250gr' | '1kg'
  quantity: number
  unit_price: number
  promised_date: string
  notes: string
}

const emptyForm: OrderForm = {
  customer_id: null,
  customer_search: '',
  new_customer_name: '',
  new_customer_phone: '',
  new_customer_address: '',
  new_customer_district: '',
  new_customer_dni: '',
  new_customer_ruc: '',
  new_customer_payment_mode: 'cash',
  seller_product_id: null,
  presentation: 'granel',
  quantity: 1,
  unit_price: 0,
  promised_date: '',
  notes: '',
}

const statusBadge: Record<ItemStatus, string> = {
  pactado: 'bg-amber-100 text-amber-800',
  entregado: 'bg-green-100 text-green-800',
  cancelado: 'bg-red-100 text-red-800',
}

const statusLabel: Record<ItemStatus, string> = {
  pactado: 'Pactado',
  entregado: 'Entregado',
  cancelado: 'Cancelado',
}

function summarizeItemStatuses(items: OrderItem[]): string {
  const counts = items.reduce<Record<string, number>>((acc, item) => {
    acc[item.status] = (acc[item.status] || 0) + 1
    return acc
  }, {})
  return Object.entries(counts)
    .map(([status, count]) => `${statusLabel[status as ItemStatus]} (${count})`)
    .join(', ')
}

function orderTotal(order: Order): number {
  return order.items.reduce((sum, item) => sum + Number(item.unit_price) * item.quantity, 0)
}

const ORDERS_EXPORT_COLUMNS: ExportColumn<Order>[] = [
  { key: 'id', label: 'ID', format: (o) => `#${o.id}` },
  { key: 'agreed_date', label: 'Fecha', format: (o) => new Date(o.agreed_date).toLocaleDateString() },
  { key: 'customer_name', label: 'Cliente', format: (o) => o.customer_name || 'Sin cliente' },
  { key: 'customer_district', label: 'Distrito', format: (o) => o.customer_district || '-' },
  { key: 'items_count', label: 'Cant. Items', format: (o) => String(o.items.length) },
  { key: 'status', label: 'Estado', format: (o) => summarizeItemStatuses(o.items) },
  { key: 'total', label: 'Total', format: (o) => `$${orderTotal(o).toFixed(2)}` },
]

export default function OrdersPage() {
  const [orders, setOrders] = useState<Order[]>([])
  const [customers, setCustomers] = useState<Customer[]>([])
  const [sellerProducts, setSellerProducts] = useState<SellerProduct[]>([])
  const [loading, setLoading] = useState(true)

  const [showForm, setShowForm] = useState(false)
  const [step, setStep] = useState<Step>('customer')
  const [form, setForm] = useState<OrderForm>(emptyForm)
  const [cart, setCart] = useState<CartItem[]>([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const [deliverItem, setDeliverItem] = useState<OrderItem | null>(null)
  const [deliverSaleDate, setDeliverSaleDate] = useState(new Date().toISOString().split('T')[0])
  const [deliverPaymentDate, setDeliverPaymentDate] = useState('')
  const [deliverExpectedDate, setDeliverExpectedDate] = useState('')
  const [deliverPartial, setDeliverPartial] = useState(0)
  const [deliverNotes, setDeliverNotes] = useState('')
  const [deliverSaving, setDeliverSaving] = useState(false)
  const [deliverError, setDeliverError] = useState('')

  const [cancelItem, setCancelItem] = useState<OrderItem | null>(null)
  const [cancelReason, setCancelReason] = useState('')
  const [cancelSaving, setCancelSaving] = useState(false)

  const fetchData = async () => {
    try {
      const [ordersRes, customersRes, productsRes] = await Promise.all([
        ordersApi.list(),
        customersApi.list(),
        sellerProductsApi.list(),
      ])
      setOrders(ordersRes.data.orders || [])
      setCustomers(customersRes.data.customers || [])
      setSellerProducts(productsRes.data.seller_products || [])
    } catch (err) {
      console.error('Error fetching orders:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  const filteredCustomers = useMemo(() => {
    if (!form.customer_search) return customers
    const q = form.customer_search.toLowerCase()
    return customers.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        (c.nickname && c.nickname.toLowerCase().includes(q)) ||
        (c.district && c.district.toLowerCase().includes(q)) ||
        (c.phone && c.phone.includes(q))
    )
  }, [customers, form.customer_search])

  const cartTotal = useMemo(
    () => cart.reduce((sum, item) => sum + item.unit_price * item.quantity, 0),
    [cart]
  )

  const selectedProduct = useMemo(
    () => sellerProducts.find((p) => p.id === form.seller_product_id),
    [sellerProducts, form.seller_product_id]
  )

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setForm((prev) => ({
      ...prev,
      [name]: name === 'quantity' || name === 'unit_price' ? Number(value) : value,
      ...(name === 'customer_search' ? { customer_id: null } : {}),
    }))
  }

  const selectCustomer = (c: Customer) => {
    setForm((prev) => ({
      ...prev,
      customer_id: c.id,
      customer_search: `${c.name}${c.nickname ? ` (${c.nickname})` : ''}`,
    }))
    setStep('product')
  }

  const selectNewCustomer = () => setStep('product')

  const openNew = () => {
    setForm(emptyForm)
    setCart([])
    setStep('customer')
    setError('')
    setShowForm(true)
  }

  const selectProduct = (p: SellerProduct) => {
    setForm((prev) => ({
      ...prev,
      seller_product_id: p.id,
      unit_price: p.real_price,
      presentation: p.presentation as 'granel' | '250gr' | '1kg',
    }))
  }

  const handleAddToCart = () => {
    if (!form.seller_product_id) {
      setError('Por favor selecciona un producto')
      return
    }
    const product = sellerProducts.find((p) => p.id === form.seller_product_id)
    if (!product) return

    setCart((prev) => [
      ...prev,
      {
        seller_product_id: product.id,
        product_name: product.name,
        presentation: product.presentation as 'granel' | '250gr' | '1kg',
        quantity: form.quantity,
        unit_price: form.unit_price,
        recommended_price: product.recommended_price,
      },
    ])
    setError('')
    setForm((prev) => ({ ...prev, seller_product_id: null, presentation: 'granel', quantity: 1, unit_price: 0 }))
  }

  const handleRemoveFromCart = (index: number) => {
    setCart((prev) => prev.filter((_, i) => i !== index))
  }

  const handleConfirmOrder = async () => {
    setSaving(true)
    setError('')
    try {
      let customerId = form.customer_id

      if (!customerId && form.new_customer_name.trim()) {
        const custRes = await customersApi.create({
          name: form.new_customer_name,
          phone: form.new_customer_phone || null,
          address: form.new_customer_address || null,
          district: form.new_customer_district || null,
          dni: form.new_customer_dni || null,
          ruc: form.new_customer_ruc || null,
          payment_mode: form.new_customer_payment_mode,
        })
        customerId = custRes.data.id
      }

      if (!customerId) {
        setError('Por favor selecciona o crea un cliente')
        setSaving(false)
        return
      }

      if (cart.length === 0) {
        setError('Agrega al menos un producto')
        setSaving(false)
        return
      }

      // Silent, non-blocking GPS capture: if permission is denied or the
      // request times out, this resolves to null and the order is still
      // registered without coordinates.
      const pos = await getCurrentPositionSafe()

      await ordersApi.create({
        customer_id: customerId,
        promised_date: form.promised_date || null,
        notes: form.notes || undefined,
        gps_latitude: pos?.latitude ?? null,
        gps_longitude: pos?.longitude ?? null,
        items: cart.map((item) => ({
          seller_product_id: item.seller_product_id,
          presentation: item.presentation,
          quantity: item.quantity,
          unit_price: item.unit_price,
        })),
      })

      setCart([])
      setShowForm(false)
      fetchData()
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al registrar el pedido')
    } finally {
      setSaving(false)
    }
  }

  const openDeliverModal = (item: OrderItem) => {
    setDeliverItem(item)
    setDeliverSaleDate(new Date().toISOString().split('T')[0])
    setDeliverPaymentDate('')
    setDeliverExpectedDate('')
    setDeliverPartial(0)
    setDeliverNotes('')
    setDeliverError('')
  }

  const handleDeliverSubmit = async () => {
    if (!deliverItem) return
    setDeliverSaving(true)
    setDeliverError('')
    try {
      await ordersApi.convertToSale([
        {
          order_item_id: deliverItem.id,
          sale_date: deliverSaleDate || undefined,
          payment_date: deliverPaymentDate || null,
          expected_payment_date: deliverExpectedDate || null,
          partial_payments: deliverPartial || 0,
          notes: deliverNotes || null,
        },
      ])
      setDeliverItem(null)
      fetchData()
    } catch (err: any) {
      setDeliverError(err.response?.data?.error || 'Error al registrar la venta')
    } finally {
      setDeliverSaving(false)
    }
  }

  const openCancelModal = (item: OrderItem) => {
    setCancelItem(item)
    setCancelReason('')
  }

  const handleCancelSubmit = async () => {
    if (!cancelItem || !cancelReason.trim()) return
    setCancelSaving(true)
    try {
      await ordersApi.cancelItems([cancelItem.id], cancelReason.trim())
      setCancelItem(null)
      fetchData()
    } catch (err: any) {
      alert(err.response?.data?.error || 'Error al cancelar el producto')
    } finally {
      setCancelSaving(false)
    }
  }

  if (loading) {
    return <div className="text-center py-8">Cargando...</div>
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-espresso-800">Pedidos</h1>
        <button
          onClick={openNew}
          className="px-4 py-2 bg-terracotta-600 text-white rounded-md hover:bg-terracotta-700 text-sm font-medium"
        >
          + Nuevo Pedido
        </button>
      </div>

      {/* Create Order Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-cream-50 rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-espresso-200">
              <h2 className="text-lg font-semibold text-espresso-800">Registrar Pedido</h2>
              <div className="flex mt-3 space-x-2">
                {(['customer', 'product', 'review'] as Step[]).map((s, i) => (
                  <div
                    key={s}
                    className={`flex items-center text-xs ${
                      step === s ? 'text-terracotta-700 font-semibold' : 'text-espresso-500'
                    }`}
                  >
                    <span
                      className={`w-5 h-5 rounded-full flex items-center justify-center mr-1 ${
                        step === s ? 'bg-terracotta-600 text-white' : 'bg-cream-200 text-espresso-600'
                      }`}
                    >
                      {i + 1}
                    </span>
                    {s.charAt(0).toUpperCase() + s.slice(1)}
                    {i < 2 && <span className="mx-1">→</span>}
                  </div>
                ))}
              </div>
            </div>

            <div className="px-6 py-4">
              {error && <div className="bg-red-50 text-red-700 px-4 py-2 rounded text-sm mb-4">{error}</div>}

              {/* Step: Customer */}
              {step === 'customer' && (
                <div className="space-y-4">
                  <h3 className="text-sm font-medium text-espresso-700">Selecciona o crea un cliente</h3>

                  <input
                    type="text"
                    placeholder="Buscar por nombre, apodo, distrito o teléfono..."
                    value={form.customer_search}
                    onChange={handleChange}
                    name="customer_search"
                    className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                  />

                  {form.customer_id ? (
                    <div className="bg-green-50 border border-green-200 rounded-md p-3 text-sm text-green-800">
                      Cliente seleccionado: <strong>{form.customer_search}</strong>
                      <button
                        onClick={() => {
                          if (cart.length > 0 && !confirm('Cambiar de cliente vaciará los productos ya agregados. ¿Continuar?')) {
                            return
                          }
                          setCart([])
                          setForm((p) => ({ ...p, customer_id: null, customer_search: '' }))
                        }}
                        className="ml-2 text-green-600 underline"
                      >
                        Cambiar
                      </button>
                    </div>
                  ) : (
                    <div className="max-h-48 overflow-y-auto border border-espresso-200 rounded-md divide-y divide-espresso-100">
                      {filteredCustomers.length === 0 ? (
                        <div className="px-3 py-4 text-sm text-espresso-500">No se encontraron clientes</div>
                      ) : (
                        filteredCustomers.map((c) => (
                          <button
                            key={c.id}
                            onClick={() => selectCustomer(c)}
                            className="w-full text-left px-3 py-2 hover:bg-terracotta-50 text-sm flex justify-between"
                          >
                            <span>
                              {c.name}
                              {c.nickname && <span className="text-espresso-500 ml-1">({c.nickname})</span>}
                            </span>
                            <span className="text-espresso-500">
                              {c.district} · {c.phone || '-'}
                            </span>
                          </button>
                        ))
                      )}
                    </div>
                  )}

                  <div className="border-t border-espresso-200 pt-4">
                    <p className="text-xs font-medium text-espresso-500 uppercase mb-3">O registrar nuevo cliente</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <input
                        type="text"
                        name="new_customer_name"
                        value={form.new_customer_name}
                        onChange={handleChange}
                        placeholder="Nombre *"
                        className="col-span-2 border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                      />
                      <input
                        type="text"
                        name="new_customer_phone"
                        value={form.new_customer_phone}
                        onChange={handleChange}
                        placeholder="Teléfono"
                        className="border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                      />
                      <input
                        type="text"
                        name="new_customer_district"
                        value={form.new_customer_district}
                        onChange={handleChange}
                        placeholder="Distrito"
                        className="border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                      />
                      <input
                        type="text"
                        name="new_customer_address"
                        value={form.new_customer_address}
                        onChange={handleChange}
                        placeholder="Dirección"
                        className="col-span-2 border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                      />
                      <div>
                        <label className="block text-xs text-espresso-500 mb-1">Modo de Pago</label>
                        <select
                          name="new_customer_payment_mode"
                          value={form.new_customer_payment_mode}
                          onChange={handleChange}
                          className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                        >
                          <option value="cash">Efectivo</option>
                          <option value="credit">Crédito</option>
                        </select>
                      </div>
                    </div>
                    {form.new_customer_name.trim() && (
                      <button
                        onClick={selectNewCustomer}
                        className="mt-3 px-4 py-2 bg-terracotta-600 text-white rounded-md text-sm hover:bg-terracotta-700"
                      >
                        Continuar con nuevo cliente →
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Step: Product */}
              {step === 'product' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium text-espresso-700">Seleccionar producto</h3>
                    <button onClick={() => setStep('customer')} className="text-xs text-terracotta-700 underline">
                      ← Volver
                    </button>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-espresso-700 mb-1">
                      Fecha prometida de entrega
                    </label>
                    <input
                      type="date"
                      name="promised_date"
                      value={form.promised_date}
                      onChange={handleChange}
                      className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                    />
                  </div>

                  {sellerProducts.length === 0 ? (
                    <p className="text-sm text-espresso-500">
                      No tienes productos en tu catálogo todavía. Ve a "Productos" para agregar los que vas a vender.
                    </p>
                  ) : (
                    <div className="grid grid-cols-1 gap-2 max-h-60 overflow-y-auto">
                      {sellerProducts.map((p) => (
                        <button
                          key={p.id}
                          onClick={() => selectProduct(p)}
                          className={`text-left p-3 rounded-md border text-sm ${
                            form.seller_product_id === p.id
                              ? 'border-terracotta-500 bg-terracotta-50 ring-1 ring-terracotta-500'
                              : 'border-espresso-200 hover:border-espresso-300'
                          }`}
                        >
                          <div className="flex justify-between">
                            <span className="font-medium">{p.name}</span>
                            <span className="text-espresso-600">${Number(p.real_price).toFixed(2)}</span>
                          </div>
                          <div className="text-xs text-espresso-500 mt-1">
                            Presentación: {p.presentation} · Precio sugerido: ${Number(p.recommended_price).toFixed(2)}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {form.seller_product_id && (
                    <>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-espresso-700 mb-1">Presentación</label>
                          <input
                            type="text"
                            disabled
                            readOnly
                            value={selectedProduct ? selectedProduct.presentation : form.presentation}
                            title="La presentación viene definida por el producto seleccionado"
                            className="w-full border border-espresso-200 rounded-md px-3 py-2 text-sm bg-cream-100 text-espresso-500 cursor-not-allowed"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-espresso-700 mb-1">Cantidad (1-500)</label>
                          <input
                            type="number"
                            name="quantity"
                            value={form.quantity}
                            onChange={handleChange}
                            min={1}
                            max={500}
                            className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-espresso-700 mb-1">Precio pactado ($)</label>
                          <input
                            type="number"
                            name="unit_price"
                            value={form.unit_price}
                            onChange={handleChange}
                            min={0}
                            step={0.01}
                            className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                          />
                        </div>
                      </div>

                      <div className="bg-cream-50 rounded-md p-3 text-sm">
                        <div className="flex justify-between font-semibold">
                          <span>Total:</span>
                          <span>${(form.unit_price * form.quantity).toFixed(2)}</span>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={handleAddToCart}
                        className="w-full px-4 py-2 bg-terracotta-600 text-white rounded-md text-sm font-medium hover:bg-terracotta-700"
                      >
                        + Agregar Producto
                      </button>
                    </>
                  )}

                  {cart.length > 0 && (
                    <div className="border-t border-espresso-200 pt-4 space-y-2">
                      <h4 className="text-sm font-medium text-espresso-700">Productos agregados ({cart.length})</h4>
                      {cart.map((item, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between bg-cream-50 rounded-md p-3 text-sm"
                        >
                          <div>
                            <div className="font-medium text-espresso-800">
                              {item.product_name} × {item.quantity} ({item.presentation})
                            </div>
                            <div className="text-xs text-espresso-500">
                              Precio pactado: ${item.unit_price.toFixed(2)} · Total: $
                              {(item.unit_price * item.quantity).toFixed(2)}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveFromCart(index)}
                            className="text-red-600 hover:text-red-900 text-xs ml-3"
                          >
                            Quitar
                          </button>
                        </div>
                      ))}
                      <div className="flex justify-between font-semibold text-sm pt-1">
                        <span>Total del pedido:</span>
                        <span>${cartTotal.toFixed(2)}</span>
                      </div>
                      <button
                        onClick={() => setStep('review')}
                        className="w-full px-4 py-2 bg-terracotta-600 text-white rounded-md text-sm font-medium hover:bg-terracotta-700"
                      >
                        Continuar a Revisar →
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Step: Review */}
              {step === 'review' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium text-espresso-700">Revisar y Confirmar</h3>
                    <button onClick={() => setStep('product')} className="text-xs text-terracotta-700 underline">
                      ← Volver
                    </button>
                  </div>

                  <div className="bg-cream-50 rounded-md p-4 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-espresso-500">Cliente:</span>
                      <span className="font-medium">{form.customer_search || form.new_customer_name}</span>
                    </div>
                    {form.promised_date && (
                      <div className="flex justify-between">
                        <span className="text-espresso-500">Entrega prometida:</span>
                        <span className="font-medium">{form.promised_date}</span>
                      </div>
                    )}
                    <div className="border-t border-espresso-200 pt-2 space-y-2">
                      {cart.map((item, index) => (
                        <div key={index} className="flex justify-between">
                          <span>
                            {item.product_name} × {item.quantity} ({item.presentation})
                          </span>
                          <span>${(item.unit_price * item.quantity).toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                    <div className="flex justify-between border-t border-espresso-200 pt-2 font-semibold">
                      <span>Total:</span>
                      <span>${cartTotal.toFixed(2)}</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-espresso-700 mb-1">Notas (opcional)</label>
                    <textarea
                      name="notes"
                      value={form.notes}
                      onChange={handleChange}
                      rows={2}
                      placeholder="Detalles del acuerdo..."
                      className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                    />
                  </div>

                  <button
                    onClick={handleConfirmOrder}
                    disabled={saving}
                    className="w-full px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 disabled:opacity-50"
                  >
                    {saving ? 'Guardando...' : 'Confirmar Pedido'}
                  </button>
                </div>
              )}

              {step !== 'review' && (
                <div className="flex justify-end pt-4 border-t border-espresso-200 mt-4">
                  <button
                    onClick={() => {
                      setCart([])
                      setShowForm(false)
                    }}
                    className="px-4 py-2 border border-espresso-300 rounded-md text-sm text-espresso-700 hover:bg-cream-50"
                  >
                    Cancelar
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Orders List */}
      {orders.length === 0 ? (
        <div className="bg-cream-50 shadow rounded-lg p-8 text-center">
          <p className="text-espresso-500">No hay pedidos aún. Registra tu primer pedido para comenzar.</p>
        </div>
      ) : (
        <>
        <ExportBar data={orders} columns={ORDERS_EXPORT_COLUMNS} filename="pedidos" title="Pedidos" />
        <div className="space-y-4">
          {orders.map((order) => (
            <div key={order.id} className="bg-cream-50 shadow rounded-lg overflow-hidden">
              <div className="px-4 py-3 bg-cream-50 border-b border-espresso-100 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <span className="font-medium text-espresso-800">Pedido #{order.id}</span>
                  <span className="text-espresso-500 text-sm ml-2">
                    {order.customer_name || 'Sin cliente'}
                    {order.customer_district && ` · ${order.customer_district}`}
                  </span>
                </div>
                <div className="text-xs text-espresso-500">
                  Pactado: {new Date(order.agreed_date).toLocaleDateString()}
                  {order.promised_date && (
                    <span className="ml-3">Entrega prometida: {new Date(order.promised_date).toLocaleDateString()}</span>
                  )}
                </div>
              </div>
              {order.notes && (
                <div className="px-4 py-2 text-sm text-espresso-600 border-b border-espresso-100">{order.notes}</div>
              )}
              <div className="divide-y divide-espresso-100">
                {order.items.map((item) => (
                  <div key={item.id} className="px-4 py-3 flex flex-wrap items-center justify-between gap-2">
                    <div className="text-sm">
                      <span className="font-medium text-espresso-800">
                        {item.product_name} × {item.quantity} ({item.presentation})
                      </span>
                      <span className="text-espresso-500 ml-2">
                        ${Number(item.unit_price).toFixed(2)} c/u · Total: $
                        {(Number(item.unit_price) * item.quantity).toFixed(2)}
                      </span>
                      {item.status === 'cancelado' && item.cancellation_reason && (
                        <div className="text-xs text-red-600 mt-1">Motivo: {item.cancellation_reason}</div>
                      )}
                      {item.status === 'entregado' && item.sale_id && (
                        <div className="text-xs text-green-600 mt-1">Venta #{item.sale_id}</div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusBadge[item.status]}`}>
                        {statusLabel[item.status]}
                      </span>
                      {item.status === 'pactado' && (
                        <>
                          <button
                            onClick={() => openDeliverModal(item)}
                            className="text-green-700 hover:text-green-900 text-xs font-medium"
                          >
                            Entregado
                          </button>
                          <button
                            onClick={() => openCancelModal(item)}
                            className="text-red-600 hover:text-red-800 text-xs font-medium"
                          >
                            Cancelar
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        </>
      )}

      {/* Deliver -> Convert to Sale Modal */}
      {deliverItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-cream-50 rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="px-6 py-4 border-b border-espresso-200">
              <h2 className="text-lg font-semibold text-espresso-800">Registrar Entrega como Venta</h2>
              <p className="text-sm text-espresso-500 mt-1">
                {deliverItem.product_name} × {deliverItem.quantity} ({deliverItem.presentation}) · $
                {(Number(deliverItem.unit_price) * deliverItem.quantity).toFixed(2)}
              </p>
            </div>
            <div className="px-6 py-4 space-y-4">
              {deliverError && (
                <div className="bg-red-50 text-red-700 px-4 py-2 rounded text-sm">{deliverError}</div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-espresso-700 mb-1">Fecha de Venta</label>
                  <input
                    type="date"
                    value={deliverSaleDate}
                    onChange={(e) => setDeliverSaleDate(e.target.value)}
                    className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-espresso-700 mb-1">Fecha de Pago Esperada</label>
                  <input
                    type="date"
                    value={deliverExpectedDate}
                    onChange={(e) => setDeliverExpectedDate(e.target.value)}
                    className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-espresso-700 mb-1">Pagos Parciales ($)</label>
                  <input
                    type="number"
                    value={deliverPartial}
                    onChange={(e) => setDeliverPartial(Number(e.target.value))}
                    min={0}
                    step={0.01}
                    className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-espresso-700 mb-1">Fecha de Pago</label>
                  <input
                    type="date"
                    value={deliverPaymentDate}
                    onChange={(e) => setDeliverPaymentDate(e.target.value)}
                    className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-espresso-700 mb-1">Notas (opcional)</label>
                <input
                  type="text"
                  value={deliverNotes}
                  onChange={(e) => setDeliverNotes(e.target.value)}
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4 border-t border-espresso-200">
                <button
                  type="button"
                  onClick={() => setDeliverItem(null)}
                  className="px-4 py-2 border border-espresso-300 rounded-md text-sm text-espresso-700 hover:bg-cream-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDeliverSubmit}
                  disabled={deliverSaving}
                  className="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 disabled:opacity-50"
                >
                  {deliverSaving ? 'Guardando...' : 'Confirmar Venta'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Item Modal */}
      {cancelItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-cream-50 rounded-lg shadow-xl w-full max-w-sm mx-4">
            <div className="px-6 py-4 border-b border-espresso-200">
              <h2 className="text-lg font-semibold text-espresso-800">Cancelar Producto del Pedido</h2>
              <p className="text-sm text-espresso-500 mt-1">
                {cancelItem.product_name} × {cancelItem.quantity} ({cancelItem.presentation})
              </p>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-espresso-700 mb-1">Razón de la cancelación *</label>
                <textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  rows={3}
                  placeholder="Describe por qué se canceló..."
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4 border-t border-espresso-200">
                <button
                  type="button"
                  onClick={() => setCancelItem(null)}
                  className="px-4 py-2 border border-espresso-300 rounded-md text-sm text-espresso-700 hover:bg-cream-50"
                >
                  Volver
                </button>
                <button
                  onClick={handleCancelSubmit}
                  disabled={cancelSaving || !cancelReason.trim()}
                  className="px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 disabled:opacity-50"
                >
                  {cancelSaving ? 'Guardando...' : 'Confirmar Cancelación'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
